import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Image, Video } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadedUrl('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadedUrl(`${BACKEND_URL}${response.data.url}`);
      toast.success('File uploaded successfully!');
      setSelectedFile(null);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const isImage = selectedFile && selectedFile.type.startsWith('image/');
  const isVideo = selectedFile && selectedFile.type.startsWith('video/');

  return (
    <div data-testid="content-upload">
      <div className="mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Upload Content</h2>
        <p className="text-maroon/70 mt-2">Upload images and videos from Canva or other sources</p>
      </div>

      <div className="max-w-2xl">
        <div className="card">
          <div className="border-2 border-dashed border-maroon/30 rounded-sm p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-terracotta/10 mb-4">
              {isImage ? <Image size={40} className="text-terracotta" /> :
               isVideo ? <Video size={40} className="text-terracotta" /> :
               <Upload size={40} className="text-terracotta" />}
            </div>
            
            <h3 className="font-heading font-bold text-xl text-maroon mb-2">
              {selectedFile ? selectedFile.name : 'Select a file to upload'}
            </h3>
            
            <p className="text-maroon/70 mb-6">
              {selectedFile ? `Size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : 'Images, videos, and other content'}
            </p>

            <div className="space-y-4">
              <Label htmlFor="file-upload">
                <div className="btn-secondary cursor-pointer inline-block">
                  Choose File
                </div>
              </Label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,video/*"
                data-testid="file-upload-input"
              />

              {selectedFile && (
                <div>
                  <Button 
                    onClick={handleUpload} 
                    disabled={uploading}
                    className="btn-primary"
                    data-testid="upload-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {uploadedUrl && (
            <div className="mt-6 p-4 bg-forest/10 border border-forest/20 rounded-sm">
              <p className="text-sm font-semibold text-forest mb-2">File uploaded successfully!</p>
              <div className="flex items-center space-x-2">
                <input 
                  type="text" 
                  value={uploadedUrl} 
                  readOnly 
                  className="flex-1 bg-white border border-maroon/20 rounded-sm px-3 py-2 text-sm"
                  data-testid="uploaded-url"
                />
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(uploadedUrl);
                    toast.success('URL copied to clipboard!');
                  }}
                  size="sm"
                  className="btn-secondary"
                >
                  Copy URL
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 card bg-sand/30">
          <h3 className="font-heading font-bold text-lg text-maroon mb-3">How to use uploaded content:</h3>
          <ol className="list-decimal list-inside space-y-2 text-maroon/70 text-sm">
            <li>Upload your image or video file using the form above</li>
            <li>Copy the generated URL</li>
            <li>Use this URL when creating packages, blog posts, or destinations</li>
            <li>The content will be served from your Chimba Travel platform</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ContentUpload;